import React, { ReactNode, useCallback } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import InputField, { InputFieldProps } from 'components/styleguide/input_field/InputField';

const styles = require('./SelectionButton.module.scss');

type UserType = 'talent' | 'company' | 'neutral';

export interface SelectionButtonProps {
  children?: ReactNode;
  containerClassName?: string;
  context?: UserType;
  description: string;
  href?: string;
  icon: string;
  id?: string;
  inputFields?: Array<InputFieldProps>;
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
  name?: string;
  onClick?: () => void;
  role?: 'button' | 'radio';
  selected?: boolean;
  title: string;
  value?: string;
}

const SelectionButton: React.FC<SelectionButtonProps> = ({
  children,
  containerClassName,
  context = 'neutral',
  description,
  href,
  icon,
  id,
  inputFields,
  method,
  name,
  onClick,
  role: roleProp,
  selected = false,
  title,
  value,
}) => {
  const handleKeyDown = useCallback((e: any) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (href) {
        window.location.href = href;
      } else {
        onClick?.();
      }
    }
  }, [href, onClick]);

  const wrapperClasses = classNames(styles.wrapper, containerClassName, {
    [styles.selected]: selected,
    [styles[context]]: context,
  });

  const ButtonWrapper = href ? 'a' : 'div';
  const effectiveRole = href ? undefined : (roleProp || 'button');
  const selectionAttr = effectiveRole === 'radio'
    ? { 'aria-checked': selected }
    : { 'aria-pressed': selected };

  return (
    <ButtonWrapper
      id={id}
      className={wrapperClasses}
      onClick={href ? undefined : onClick}
      href={href}
      {...(method && { 'data-method': method })}
      role={effectiveRole}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      {...(href ? {} : selectionAttr)}
      aria-label={title}
    >
      {!href && name && value && (
        <input type="hidden" name={name} value={selected ? value : ''} />
      )}
      <div className={styles.content}>
        {icon && (
          <div className={styles.iconContainer}>
            <Icon name={icon} color="grey-700" size="md" aria-hidden="true" />
          </div>
        )}
        <div className={styles.textContainer}>
          <h3 className={styles.title}>{title}</h3>
          {description && <p className={styles.description}>{description}</p>}
        </div>
      </div>
      {selected && (children || inputFields) && (
        <div className={styles.expandedContent}>
          {children}
          {inputFields?.map((field, index) => (
            <InputField
              key={index}
              {...field}
              name={field.name || `input_${index}`}
              required={selected ? field.required : false}
            />
          ))}
        </div>
      )}
    </ButtonWrapper>
  );
};

export default SelectionButton;
